import { questionSummaryKey, removeAccents } from '@arnold/core';
import type { AnswerGroup } from '../../types';
import { ColumnType, type TableColumn } from './types';

export type ColumnValue = string | number | undefined;

export const isSearchableColumn = (column: TableColumn) =>
  [ColumnType.LABEL, ColumnType.TEAM, ColumnType.CHOICES, ColumnType.TEXT, ColumnType.THEMES].includes(column.type);

export const normalizeSearchText = (value: string) => removeAccents(value).toLocaleLowerCase().trim();

/** Plain presentation values, not cell markup or a second report-calculation pipeline.
 * Never inspect respondent IDs, private metadata or individual members of anonymous pools.
 */
export function getColumnValue(group: AnswerGroup, column: TableColumn): ColumnValue {
  const summary =
    'questionIndex' in column
      ? group.questionSummaries.get(questionSummaryKey(column.questionIndex, column.statementIndex))
      : undefined;
  switch (column.type) {
    case ColumnType.CUSTOM_METRIC:
      return group.customMetrics[column.metricId]?.value;
    case ColumnType.QUESTION_ANSWER_COUNT:
      return summary?.respondingChannelIds.size ?? 0;
    case ColumnType.LABEL:
      return group.label || undefined;
    case ColumnType.TEAM:
      return group.teamNames.join('\n') || undefined;
    case ColumnType.RESPONDENT_COUNT:
      return group.respondentCount;
    case ColumnType.ANSWER_COUNT:
      return group.answerCount;
    case ColumnType.RESPONSE_RATE:
      return group.metrics.responseRate;
    case ColumnType.NPS:
    case ColumnType.MINI_NPS:
      return summary?.nps.value;
    case ColumnType.SCORE:
      return summary?.average.value;
    case ColumnType.CHOICES:
      // Unused option labels are definitions, not answers to search for.
      return (
        summary?.choices
          .filter((choice) => choice.count > 0)
          .map((choice) => choice.label)
          .join('\n') || undefined
      );
    case ColumnType.TEXT:
      return (
        (column.source === 'comment' ? summary?.commentEntries : summary?.textEntries)
          ?.map((entry) => entry.message[entry.source] ?? '')
          .join('\n') || undefined
      );
    case ColumnType.THEMES:
      return summary?.themes[column.source ?? 'text'].map((theme) => theme.label).join('\n') || undefined;
    default: {
      const unsupported: never = column;
      throw new Error(`Unsupported AnswerGroup column: ${JSON.stringify(unsupported)}`);
    }
  }
}

/** Rebinding an existing ID must invalidate TanStack's per-row accessor cache.
 * Labels, widths and ordering deliberately do not invalidate data values.
 */
export const getColumnDataKey = (columns: TableColumn[]) =>
  JSON.stringify(
    columns
      .map((column) =>
        JSON.stringify([
          column.id,
          column.type,
          'metricId' in column ? column.metricId : null,
          'presentation' in column ? column.presentation : null,
          'questionIndex' in column ? column.questionIndex : null,
          'statementIndex' in column ? column.statementIndex : null,
          'source' in column ? column.source : null,
        ]),
      )
      .sort(),
  );
